import { Button } from '@solinkcorp/solink-ui/components/Button';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import {
  createTreeNodes,
  getParentTagIdsAndTarget,
  isAtleastOneChild,
} from 'components/SettingsPage/LocationManagement/locationTagUtils';
import { type TagFilter } from 'components/SettingsPage/LocationManagement/types';
import { CircleXIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import useLocale from 'src/hooks/useLocale';
import { sortLocationTagFilterCategoryToEnd } from '../locationSelectorUtils';
import styles from './LocationTaggingFilters.module.sass';

interface LocationTaggingFiltersProps {
  selectedFiltersById: readonly string[];
  onSelectedFiltersByIdChange: (selectedFilters: string[]) => void;
  filters: TagFilter[];
  visibleTagsById: string[];
  disabled?: boolean;
}

export default function LocationTaggingFilters(props: LocationTaggingFiltersProps) {
  const {
    selectedFiltersById,
    onSelectedFiltersByIdChange,
    filters,
    visibleTagsById,
    disabled = false,
  } = props;

  const tagFilters = useMemo(() => createTreeNodes(filters, false), [filters]);

  const updateSelectedFilter = (id: string) => {
    const isSelected = selectedFiltersById.includes(id);
    const tagIds = getParentTagIdsAndTarget(id);
    if (!isSelected) {
      onSelectedFiltersByIdChange([...new Set([...selectedFiltersById, ...tagIds])]);
    } else {
      onSelectedFiltersByIdChange([...selectedFiltersById].filter((tagId) => !tagId.includes(id)));
    }
  };

  return (
    <div
      className={cn(styles['location-tagging-filters'], 'text-xs', {
        'cursor-not-allowed opacity-50': disabled,
      })}
    >
      {tagFilters.map((tagFilter, index) => (
        <Tag
          disabled={disabled}
          key={tagFilter.id}
          tagFilter={tagFilter}
          updateSelectedFilter={updateSelectedFilter}
          selectedFiltersById={selectedFiltersById}
          visibleTagsById={visibleTagsById}
          index={index}
          onSelectedFiltersByIdChange={onSelectedFiltersByIdChange}
        />
      ))}
    </div>
  );
}

function Tag({
  disabled = false,
  tagFilter,
  updateSelectedFilter,
  selectedFiltersById,
  visibleTagsById,
  index,
  onSelectedFiltersByIdChange,
}: {
  disabled?: boolean;
  tagFilter: TagFilter;
  updateSelectedFilter: (id: string) => void;
  selectedFiltersById: readonly string[];
  visibleTagsById: readonly string[];
  index: number;
  onSelectedFiltersByIdChange: LocationTaggingFiltersProps['onSelectedFiltersByIdChange'];
}) {
  const displayChildren =
    tagFilter.children && isAtleastOneChild(tagFilter.children, visibleTagsById);
  const isSelected = selectedFiltersById.includes(tagFilter.id);
  const locale = useLocale();

  const sortedChildrenTagFilters = useMemo(
    () => [...(tagFilter?.children || [])].sort(sortLocationTagFilterCategoryToEnd),
    [tagFilter],
  );
  return (
    <React.Fragment key={tagFilter.id}>
      {tagFilter.type === 'tag' && visibleTagsById.includes(tagFilter.id) && (
        <Button
          type="button"
          variant={isSelected ? 'default' : 'secondary'}
          size="sm"
          aria-pressed={isSelected}
          className="mb-1.5 mr-1.5"
          onClick={() => updateSelectedFilter(tagFilter.id)}
          disabled={disabled}
        >
          {tagFilter.label}
        </Button>
      )}
      {tagFilter.type === 'category' && displayChildren && (
        <div className={cn({ 'ml-4': tagFilter.depth })}>
          <div className={cn('mb-3 flex items-center', index === 0 ? 'mt-0' : 'mt-1.5')}>
            <label className="font-normal text-foreground-secondary">{tagFilter.label}</label>
            {index === 0 && !!selectedFiltersById.length && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                title="clear"
                className="ml-1.5"
                onClick={() => onSelectedFiltersByIdChange([])}
              >
                <CircleXIcon className="size-4" />
                {locale.locationSelector.clearFilters}
              </Button>
            )}
          </div>
          {tagFilter.children &&
            sortedChildrenTagFilters.map((filter, index) => (
              <Tag
                disabled={disabled}
                key={filter.id}
                tagFilter={filter}
                updateSelectedFilter={updateSelectedFilter}
                selectedFiltersById={selectedFiltersById}
                visibleTagsById={visibleTagsById}
                index={index}
                onSelectedFiltersByIdChange={onSelectedFiltersByIdChange}
              />
            ))}
        </div>
      )}
    </React.Fragment>
  );
}
